// Click-to-resize for images inside a Quill editor.
//
// Quill ships nothing for this, and the usual community module
// (quill-image-resize-module) targets Quill 1 and breaks against the Quill 2
// this app uses. It's a small enough surface to own outright rather than
// take on an unmaintained dependency.
//
// Sizes are written as `width`/`height` HTML **attributes**, not inline
// styles, on purpose: the task description this edits is also rendered in
// the reminder email through Rails' `sanitize`, whose default allowlist
// permits width/height but strips `style`. Inline styles would survive in
// the editor and then silently collapse to full size in email.

import Quill from 'quill'

const HANDLES = [
  { key: 'nw', axis: 'both', cursor: 'nwse-resize', style: { top: '-5px', left: '-5px' } },
  { key: 'ne', axis: 'both', cursor: 'nesw-resize', style: { top: '-5px', right: '-5px' } },
  { key: 'sw', axis: 'both', cursor: 'nesw-resize', style: { bottom: '-5px', left: '-5px' } },
  { key: 'se', axis: 'both', cursor: 'nwse-resize', style: { bottom: '-5px', right: '-5px' } },
  { key: 'w', axis: 'x', cursor: 'ew-resize', style: { top: 'calc(50% - 5px)', left: '-5px' } },
  { key: 'e', axis: 'x', cursor: 'ew-resize', style: { top: 'calc(50% - 5px)', right: '-5px' } },
  { key: 'n', axis: 'y', cursor: 'ns-resize', style: { left: 'calc(50% - 5px)', top: '-5px' } },
  { key: 's', axis: 'y', cursor: 'ns-resize', style: { left: 'calc(50% - 5px)', bottom: '-5px' } },
]

const MIN_SIZE = 32

// Registered once, globally. The stock image blot keeps only `alt`, so a
// width/height set here would be dropped the moment Quill re-rendered the
// blot from its Delta — the resize would appear to work and then silently
// undo itself on the next edit.
let formatRegistered = false
function registerResizableImageFormat() {
  if (formatRegistered) return
  formatRegistered = true

  const BaseImage = Quill.import('formats/image')
  const KEPT = ['alt', 'width', 'height']

  class ResizableImage extends BaseImage {
    static formats(domNode) {
      return KEPT.reduce((formats, attr) => {
        if (domNode.hasAttribute(attr)) formats[attr] = domNode.getAttribute(attr)
        return formats
      }, {})
    }

    format(name, value) {
      if (!KEPT.includes(name)) return super.format(name, value)
      if (value) this.domNode.setAttribute(name, value)
      else this.domNode.removeAttribute(name)
      return undefined
    }
  }

  Quill.register(ResizableImage, true)
}

/**
 * @param {Quill} quill
 * @param {HTMLElement} root  positioned ancestor the overlay is placed in
 * @param {Function} onChange called after a resize commits
 * @returns {Function} cleanup
 */
export function attachImageResize(quill, root, onChange) {
  registerResizableImageFormat()

  let selected = null
  let drag = null

  const overlay = document.createElement('div')
  overlay.className = 'qe-resizer'
  overlay.style.display = 'none'

  const label = document.createElement('span')
  label.className = 'qe-resizer-label'
  overlay.appendChild(label)

  const handleEls = HANDLES.map((handle) => {
    const el = document.createElement('span')
    el.className = 'qe-resizer-handle'
    el.dataset.handle = handle.key
    el.style.cursor = handle.cursor
    Object.assign(el.style, handle.style)
    el.addEventListener('pointerdown', (event) => startDrag(event, handle))
    overlay.appendChild(el)
    return el
  })

  root.appendChild(overlay)

  function position() {
    if (!selected) return
    const imageBox = selected.getBoundingClientRect()
    const rootBox = root.getBoundingClientRect()
    overlay.style.top = `${imageBox.top - rootBox.top}px`
    overlay.style.left = `${imageBox.left - rootBox.left}px`
    overlay.style.width = `${imageBox.width}px`
    overlay.style.height = `${imageBox.height}px`
    label.textContent = `${Math.round(imageBox.width)} x ${Math.round(imageBox.height)}`
  }

  function select(image) {
    selected = image
    overlay.style.display = 'block'
    position()
  }

  function deselect() {
    selected = null
    drag = null
    overlay.style.display = 'none'
  }

  function startDrag(event, handle) {
    if (!selected) return
    event.preventDefault()
    event.stopPropagation()

    const box = selected.getBoundingClientRect()
    drag = {
      handle,
      startX: event.clientX,
      startY: event.clientY,
      startWidth: box.width,
      startHeight: box.height,
      // Corner drags keep the picture's proportions; edge drags are
      // deliberately free so width and height can be set independently.
      ratio: box.height / (box.width || 1),
      pointerId: event.pointerId,
    }
    event.currentTarget.setPointerCapture?.(event.pointerId)
    window.addEventListener('pointermove', onDragMove)
    window.addEventListener('pointerup', onDragEnd)
  }

  function onDragMove(event) {
    if (!drag || !selected) return

    // Left/top handles grow the image as the pointer moves away from the
    // opposite edge, so their delta is inverted.
    const signX = drag.handle.key.includes('w') ? -1 : 1
    const signY = drag.handle.key.includes('n') ? -1 : 1
    const dx = (event.clientX - drag.startX) * signX
    const dy = (event.clientY - drag.startY) * signY

    let width = drag.startWidth
    let height = drag.startHeight

    // Clamped to the editor's content width rather than left unbounded. The
    // stylesheet caps images at `max-width: 100%`, so an over-wide `width`
    // would be shrunk on render while the `height` attribute stayed put —
    // the picture would silently distort. Never allowing the overflow in the
    // first place keeps the two attributes honest.
    const maxWidth = Math.max(MIN_SIZE, quill.root.clientWidth - 2)

    if (drag.handle.axis === 'both') {
      width = Math.min(maxWidth, Math.max(MIN_SIZE, drag.startWidth + dx))
      height = Math.round(width * drag.ratio)
    } else if (drag.handle.axis === 'x') {
      width = Math.min(maxWidth, Math.max(MIN_SIZE, drag.startWidth + dx))
    } else {
      height = Math.max(MIN_SIZE, drag.startHeight + dy)
    }

    selected.setAttribute('width', String(Math.round(width)))
    selected.setAttribute('height', String(Math.round(height)))
    position()
  }

  function onDragEnd() {
    window.removeEventListener('pointermove', onDragMove)
    window.removeEventListener('pointerup', onDragEnd)
    if (!drag) return
    drag = null
    position()
    onChange?.()
  }

  function onEditorClick(event) {
    if (event.target instanceof HTMLImageElement && quill.isEnabled()) select(event.target)
    else deselect()
  }

  function onDocumentPointerDown(event) {
    if (!selected) return
    if (overlay.contains(event.target) || event.target === selected) return
    deselect()
  }

  function onKeyDown(event) {
    if (event.key === 'Escape') deselect()
  }

  // The image can be removed by an ordinary edit (undo, select-all delete)
  // while the overlay is still pinned over where it used to be.
  function onTextChange() {
    if (selected && !selected.isConnected) deselect()
    else position()
  }

  quill.root.addEventListener('click', onEditorClick)
  quill.root.addEventListener('scroll', position)
  document.addEventListener('pointerdown', onDocumentPointerDown, true)
  document.addEventListener('keydown', onKeyDown)
  window.addEventListener('resize', position)
  quill.on('text-change', onTextChange)

  return function cleanup() {
    quill.root.removeEventListener('click', onEditorClick)
    quill.root.removeEventListener('scroll', position)
    document.removeEventListener('pointerdown', onDocumentPointerDown, true)
    document.removeEventListener('keydown', onKeyDown)
    window.removeEventListener('resize', position)
    window.removeEventListener('pointermove', onDragMove)
    window.removeEventListener('pointerup', onDragEnd)
    quill.off('text-change', onTextChange)
    handleEls.length = 0
    overlay.remove()
  }
}
